import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/leaflet-custom.css';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { MapPin, Waves, Plus, Edit2, Trash2, Save, X, ChevronDown, GripVertical, Navigation } from 'lucide-react';
import MultiImageUpload from './MultiImageUpload';
import { BACKEND_URL } from '@/utils/apiConfig';

// Custom icons - Larger for better dragging experience
const diveCenterIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [35, 57],
  iconAnchor: [17, 57],
  popupAnchor: [1, -48],
  shadowSize: [57, 57],
  className: 'dive-center-marker'
});

const diveSiteIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [35, 57],
  iconAnchor: [17, 57],
  popupAnchor: [1, -48],
  shadowSize: [57, 57],
  className: 'dive-site-marker'
});

// Component to handle map clicks and capture map instance
const MapClickHandler = ({ onMapClick, mode, onMapReady }) => {
  const map = useMapEvents({
    click: (e) => {
      if (mode) {
        onMapClick(e.latlng);
      }
    },
  });
  
  // Set the map instance when component mounts
  useEffect(() => {
    if (map && onMapReady) {
      onMapReady(map);
    }
  }, [map, onMapReady]);
  
  return null;
};

// Component to capture map reference
// MapRefCapture is no longer needed - MapClickHandler handles map instance now

const AdminMapEditor = () => {
    const [token] = useState(localStorage.getItem('token'));
  const mapRef = useRef(null);
  
  const [mode, setMode] = useState('dive-centers'); // 'dive-centers' or 'dive-sites'
  const [diveCenters, setDiveCenters] = useState([]);
  const [diveSites, setDiveSites] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newCoordinates, setNewCoordinates] = useState(null);
  const [expandedCenters, setExpandedCenters] = useState([]);
  const [mapInstance, setMapInstance] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  
  // Form states for dive center
  const [centerForm, setCenterForm] = useState({
    name: '',
    location: '',
    country: '',
    location_id: '',
    short_description: '',
    full_description: '',
    image: '',
    website: '',
    email: '',
    phone: '',
    amenities: ''
  });
  
  // Form states for dive site
  const [siteForm, setSiteForm] = useState({
    dive_center_id: '',
    name: '',
    rating: '',
    image: '',
    images: [],
    depth: '',
    difficulty: '',
    marine_life: '',
    description: ''
  });

  useEffect(() => {
    fetchDiveCenters();
    fetchDiveSites();
    fetchLocations();
  }, []);

  const fetchDiveCenters = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/map/dive-centers`);
      const data = await response.json();
      setDiveCenters(data);
    } catch (error) {
      console.error('Error fetching dive centers:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/locations`);
      const data = await response.json();
      setLocations(data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchDiveSites = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/map/dive-sites`);
      const data = await response.json();
      setDiveSites(data);
    } catch (error) {
      console.error('Error fetching dive sites:', error);
    }
  };

  const handleMapClick = (latlng) => {
    setNewCoordinates({ lat: latlng.lat, lng: latlng.lng });
    setIsAdding(true);
  };

  // Auto-zoom to dive center when selected for adding dive sites
  const handleCenterSelection = (centerId) => {
    setSiteForm({ ...siteForm, dive_center_id: centerId });
    setSelectedCenter(centerId);
    
    // Find the selected center and zoom to it
    const center = diveCenters.find(c => c.id === centerId);
    if (center && mapRef.current) {
      mapRef.current.setView(
        [center.coordinates.lat, center.coordinates.lng],
        13, // Zoom level for detail
        { animate: true, duration: 1 }
      );
    }
  };

  const handleSaveDiveCenter = async () => {
    if (!newCoordinates) {
      alert('Please click on the map to set coordinates');
      return;
    }

    const payload = {
      ...centerForm,
      coordinates: newCoordinates,
      amenities: centerForm.amenities.split(',').map(a => a.trim()).filter(a => a)
    };

    try {
      const url = editingItem 
        ? `${BACKEND_URL}/api/map/dive-centers/${editingItem.id}`
        : `${BACKEND_URL}/api/map/dive-centers`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      // Read response once
      const data = await response.json();

      if (response.ok) {
        alert(editingItem ? 'Dive center updated!' : 'Dive center created!');
        handleCancelAdd();
        fetchDiveCenters();
      } else {
        console.error('Save error:', data);
        alert('Error: ' + (data.detail || 'Failed to save'));
      }
    } catch (error) {
      console.error('Error saving dive center:', error);
      alert('Error saving dive center: ' + error.message);
    }
  };

  const handleSaveDiveSite = async () => {
    if (!newCoordinates) {
      alert('Please click on the map to set coordinates');
      return;
    }

    if (!siteForm.dive_center_id) {
      alert('Please select a dive center');
      return;
    }

    if (!siteForm.name || !siteForm.depth || !siteForm.difficulty) {
      alert('Please fill in all required fields: Name, Depth, and Difficulty');
      return;
    }

    const payload = {
      dive_center_id: siteForm.dive_center_id,
      name: siteForm.name,
      coordinates: newCoordinates,
      rating: siteForm.rating ? parseFloat(siteForm.rating) : 4.0,
      image: siteForm.image || null,
      images: siteForm.images || [],
      depth: siteForm.depth,
      difficulty: siteForm.difficulty,
      marine_life: siteForm.marine_life ? siteForm.marine_life.split(',').map(m => m.trim()).filter(m => m) : [],
      description: siteForm.description || ''
    };

    try {
      const url = editingItem 
        ? `${BACKEND_URL}/api/map/dive-sites/${editingItem.id}`
        : `${BACKEND_URL}/api/map/dive-sites`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      // Read response once
      const data = await response.json();

      if (response.ok) {
        alert(editingItem ? 'Dive site updated!' : 'Dive site created!');
        handleCancelAdd();
        await fetchDiveSites(); // Ensure sites are refreshed
      } else {
        console.error('Save error:', data);
        alert('Error: ' + (data.detail || JSON.stringify(data)));
      }
    } catch (error) {
      console.error('Error saving dive site:', error);
      alert('Error saving dive site: ' + error.message);
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;

    const endpoint = type === 'center' ? 'dive-centers' : 'dive-sites';
    try {
      const response = await fetch(`${BACKEND_URL}/api/map/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert(`${type === 'center' ? 'Dive center' : 'Dive site'} deleted!`);
        type === 'center' ? fetchDiveCenters() : fetchDiveSites();
      }
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleEdit = (item, type) => {
    setEditingItem(item);
    setNewCoordinates(item.coordinates);
    setIsAdding(true);

    if (type === 'center') {
      setCenterForm({
        name: item.name || '',
        location: item.location || '',
        country: item.country || '',
        location_id: item.location_id || '',
        short_description: item.short_description || '',
        full_description: item.full_description || '',
        image: item.image || '',
        website: item.website || '',
        email: item.email || '',
        phone: item.phone || '',
        amenities: item.amenities?.join(', ') || ''
      });
    } else {
      setSiteForm({
        dive_center_id: item.dive_center_id || '',
        name: item.name || '',
        rating: item.rating || '',
        image: item.image || '',
        images: item.images || [],
        depth: item.depth || '',
        difficulty: item.difficulty || '',
        marine_life: item.marine_life?.join(', ') || '',
        description: item.description || ''
      });
    }
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewCoordinates(null);
    setEditingItem(null);
    setCenterForm({ name: '', location: '', country: '', location_id: '', short_description: '', full_description: '', image: '', website: '', email: '', phone: '', amenities: '' });
    setSiteForm({ dive_center_id: '', name: '', rating: '', image: '', images: [], depth: '', difficulty: '', marine_life: '', description: '' });
  };

  // Zoom to marker on button click
  const handleZoomToMarker = (coordinates, e) => {
    e.stopPropagation(); // Prevent drag events
    if (mapInstance && coordinates) {
      mapInstance.flyTo([coordinates.lat, coordinates.lng], 12, {
        duration: 1
      });
    }
  };

  // Toggle dive center expansion
  const toggleCenterExpansion = (centerId) => {
    setExpandedCenters(prev => 
      prev.includes(centerId) 
        ? prev.filter(id => id !== centerId)
        : [...prev, centerId]
    );
  };

  // Group dive sites by dive center
  const getSitesByCenter = (centerId) => {
    return diveSites.filter(site => site.dive_center_id === centerId);
  };

  // Drag and drop handlers
  const handleDragStart = (e, item, type) => {
    setDraggedItem({ item, type });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, item) => {
    e.preventDefault();
    setDragOverItem(item);
  };

  const handleDrop = async (e, targetItem, type) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.type !== type) return;
    
    const items = type === 'center' ? [...diveCenters] : [...diveSites];
    const draggedIndex = items.findIndex(i => i.id === draggedItem.item.id);
    const targetIndex = items.findIndex(i => i.id === targetItem.id);
    
    if (draggedIndex === targetIndex) return;
    
    // Reorder
    const [removed] = items.splice(draggedIndex, 1);
    items.splice(targetIndex, 0, removed);
    
    // Update state
    if (type === 'center') {
      setDiveCenters(items);
    } else {
      setDiveSites(items);
    }
    
    // Update display_order in backend using bulk endpoint
    try {
      const itemType = type === 'center' ? 'centers' : 'sites';
      const reorderData = items.map((item, index) => ({
        id: item.id,
        display_order: index
      }));
      
      const response = await fetch(`${BACKEND_URL}/api/map/reorder-items?item_type=${itemType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(reorderData)
      });
      
      if (!response.ok) {
        console.error('Error reordering items');
      }
    } catch (error) {
      console.error('Error updating order:', error);
    }
    
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Map Section */}
      <div className="lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle>Interactive Map Editor</CardTitle>
            <CardDescription>
              {isAdding 
                ? `Click on the map to set coordinates for your new ${mode === 'dive-centers' ? 'dive center' : 'dive site'}`
                : `Click "Add New" to start, or edit existing items below`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Mode Selector */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={mode === 'dive-centers' ? 'default' : 'outline'}
                onClick={() => setMode('dive-centers')}
                className="flex-1"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Dive Centers
              </Button>
              <Button
                variant={mode === 'dive-sites' ? 'default' : 'outline'}
                onClick={() => setMode('dive-sites')}
                className="flex-1"
              >
                <Waves className="w-4 h-4 mr-2" />
                Dive Sites
              </Button>
            </div>

            {/* Map */}
            <div className="relative w-full h-[500px] rounded-lg overflow-hidden border-2 border-gray-200">
              {isAdding && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1000] bg-cyan-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium">
                  {editingItem ? '🔄 Drag marker to reposition' : '📍 Click map to place marker'}
                </div>
              )}
              <MapContainer
                center={[20, 0]}
                zoom={2}
                style={{ height: '100%', width: '100%' }}
                zoomControl={true}
                scrollWheelZoom={true}
                doubleClickZoom={true}
              >
                <TileLayer
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; OpenStreetMap contributors'
                />
                
                <MapClickHandler onMapClick={handleMapClick} mode={isAdding ? mode : null} onMapReady={setMapInstance} />

                {/* Existing Dive Centers */}
                {diveCenters.map((center) => (
                  <Marker
                    key={center.id}
                    position={[center.coordinates.lat, center.coordinates.lng]}
                    icon={diveCenterIcon}
                    draggable={editingItem?.id === center.id}
                    eventHandlers={{
                      dragend: (e) => {
                        const newPos = e.target.getLatLng();
                        setNewCoordinates({ lat: newPos.lat, lng: newPos.lng });
                      }
                    }}
                  >
                    <Popup>
                      <div className="p-2">
                        <h3 className="font-bold">{center.name}</h3>
                        <p className="text-sm text-gray-600">{center.location}, {center.country}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {/* Existing Dive Sites */}
                {diveSites.map((site) => (
                  <Marker
                    key={site.id}
                    position={[site.coordinates.lat, site.coordinates.lng]}
                    icon={diveSiteIcon}
                    draggable={editingItem?.id === site.id}
                    eventHandlers={{
                      dragend: (e) => {
                        const newPos = e.target.getLatLng();
                        setNewCoordinates({ lat: newPos.lat, lng: newPos.lng });
                      }
                    }}
                  >
                    <Popup>
                      <div className="p-2">
                        <h3 className="font-bold">{site.name}</h3>
                        <p className="text-sm text-gray-600">Depth: {site.depth}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {/* New Marker Preview */}
                {newCoordinates && isAdding && (
                  <Marker
                    position={[newCoordinates.lat, newCoordinates.lng]}
                    icon={mode === 'dive-centers' ? diveCenterIcon : diveSiteIcon}
                    draggable={true}
                    eventHandlers={{
                      dragend: (e) => {
                        const newPos = e.target.getLatLng();
                        setNewCoordinates({ lat: newPos.lat, lng: newPos.lng });
                      }
                    }}
                  >
                    <Popup>
                      <div className="p-2">
                        <p className="font-bold text-green-600">New Location</p>
                        <p className="text-xs">Drag to adjust</p>
                      </div>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>

              {newCoordinates && (
                <div className="absolute bottom-4 left-4 bg-white px-4 py-2 rounded-lg shadow-lg border border-gray-300 text-sm">
                  <span className="font-semibold">Coordinates:</span> {newCoordinates.lat.toFixed(4)}, {newCoordinates.lng.toFixed(4)}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Form Section */}
      <div className="lg:col-span-1">
        {!isAdding ? (
          <Card>
            <CardHeader>
              <CardTitle>Manage {mode === 'dive-centers' ? 'Dive Centers' : 'Dive Sites'}</CardTitle>
              <CardDescription>Add, edit, or delete items</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => setIsAdding(true)} className="w-full mb-4">
                <Plus className="w-4 h-4 mr-2" />
                Add New {mode === 'dive-centers' ? 'Dive Center' : 'Dive Site'}
              </Button>

              {/* List of Items */}
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {mode === 'dive-centers' ? (
                  diveCenters.map((center) => (
                    <div 
                      key={center.id} 
                      className={`p-3 border rounded-lg bg-gray-50 transition-colors ${
                        dragOverItem?.id === center.id ? 'border-cyan-500 border-2' : ''
                      }`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, center, 'center')}
                      onDragOver={(e) => handleDragOver(e, center)}
                      onDrop={(e) => handleDrop(e, center, 'center')}
                      onDragEnd={handleDragEnd}
                      title="Drag to reorder"
                    >
                      <div className="flex items-start gap-2">
                        <GripVertical className="w-4 h-4 text-gray-400 mt-1 cursor-move flex-shrink-0" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">{center.name}</h4>
                          <p className="text-xs text-gray-600">{center.location}, {center.country}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            📍 {center.coordinates?.lat.toFixed(4)}, {center.coordinates?.lng.toFixed(4)}
                          </p>
                          <div className="flex gap-2 mt-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={(e) => handleZoomToMarker(center.coordinates, e)}
                              title="Zoom to marker"
                            >
                              <Navigation className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleEdit(center, 'center'); }}>
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); handleDelete(center.id, 'center'); }}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  // Grouped dive sites by dive center
                  diveCenters.map((center) => {
                    const centerSites = getSitesByCenter(center.id);
                    if (centerSites.length === 0) return null;
                    
                    const isExpanded = expandedCenters.includes(center.id);
                    
                    return (
                      <div key={center.id} className="border rounded-lg">
                        {/* Dive Center Header */}
                        <div 
                          className="p-3 bg-cyan-50 cursor-pointer hover:bg-cyan-100 transition-colors flex items-center justify-between"
                          onClick={() => toggleCenterExpansion(center.id)}
                        >
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm text-cyan-900">{center.name}</h4>
                            <p className="text-xs text-cyan-700">{centerSites.length} dive site{centerSites.length !== 1 ? 's' : ''}</p>
                          </div>
                          <ChevronDown className={`w-4 h-4 text-cyan-700 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                        
                        {/* Dive Sites List */}
                        {isExpanded && (
                          <div className="divide-y">
                            {centerSites.map((site) => (
                              <div 
                                key={site.id} 
                                className={`p-3 bg-gray-50 hover:bg-gray-100 transition-colors ${
                                  dragOverItem?.id === site.id ? 'border-l-4 border-l-green-500' : ''
                                }`}
                                draggable
                                onDragStart={(e) => handleDragStart(e, site, 'site')}
                                onDragOver={(e) => handleDragOver(e, site)}
                                onDrop={(e) => handleDrop(e, site, 'site')}
                                onDragEnd={handleDragEnd}
                                title="Drag to reorder"
                              >
                                <div className="flex items-start gap-2">
                                  <GripVertical className="w-4 h-4 text-gray-400 mt-1 cursor-move flex-shrink-0" />
                                  <div className="flex-1">
                                    <h5 className="font-semibold text-sm">{site.name}</h5>
                                    <p className="text-xs text-gray-600">Depth: {site.depth || 'N/A'}</p>
                                    <p className="text-xs text-gray-400 mt-1">
                                      📍 {site.coordinates?.lat.toFixed(4)}, {site.coordinates?.lng.toFixed(4)}
                                    </p>
                                    <div className="flex gap-2 mt-2">
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        onClick={(e) => handleZoomToMarker(site.coordinates, e)}
                                        title="Zoom to marker"
                                      >
                                        <Navigation className="w-3 h-3" />
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleEdit(site, 'site'); }}>
                                        <Edit2 className="w-3 h-3" />
                                      </Button>
                                      <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); handleDelete(site.id, 'site'); }}>
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>
                {editingItem ? 'Edit' : 'Add'} {mode === 'dive-centers' ? 'Dive Center' : 'Dive Site'}
              </CardTitle>
              <CardDescription>
                Fill in the details below
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 max-h-[600px] overflow-y-auto">
              {mode === 'dive-centers' ? (
                <>
                  <div>
                    <Label>Name *</Label>
                    <Input value={centerForm.name} onChange={(e) => setCenterForm({...centerForm, name: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Latitude</Label>
                      <Input 
                        type="number" 
                        step="0.000001"
                        value={newCoordinates?.lat || ''} 
                        onChange={(e) => {
                          const newLat = parseFloat(e.target.value);
                          if (!isNaN(newLat)) {
                            setNewCoordinates({
                              lat: newLat,
                              lng: newCoordinates?.lng || 0
                            });
                          }
                        }}
                        placeholder="e.g., 8.5234"
                      />
                    </div>
                    <div>
                      <Label>Longitude</Label>
                      <Input 
                        type="number" 
                        step="0.000001"
                        value={newCoordinates?.lng || ''} 
                        onChange={(e) => {
                          const newLng = parseFloat(e.target.value);
                          if (!isNaN(newLng)) {
                            setNewCoordinates({
                              lat: newCoordinates?.lat || 0,
                              lng: newLng
                            });
                          }
                        }}
                        placeholder="e.g., 123.4567"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Location *</Label>
                    <Input value={centerForm.location} onChange={(e) => setCenterForm({...centerForm, location: e.target.value})} placeholder="e.g., Panglao, Bohol" />
                  </div>
                  <div>
                    <Label>Country *</Label>
                    <Input value={centerForm.country} onChange={(e) => setCenterForm({...centerForm, country: e.target.value})} />
                  </div>
                  <div>
                    <Label>Parent Location</Label>
                    <select 
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      value={centerForm.location_id || ""}
                      onChange={(e) => setCenterForm({...centerForm, location_id: e.target.value || null})}
                    >
                      <option value="">No Location (unassigned)</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.name}, {loc.country}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Short Description *</Label>
                    <Textarea value={centerForm.short_description} onChange={(e) => setCenterForm({...centerForm, short_description: e.target.value})} rows={2} />
                  </div>
                  <div>
                    <Label>Full Description</Label>
                    <Textarea value={centerForm.full_description} onChange={(e) => setCenterForm({...centerForm, full_description: e.target.value})} rows={3} />
                  </div>
                  <div>
                    <Label>Image</Label>
                    <Input 
                      type="file" 
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          const token = localStorage.getItem("token");
                          const response = await fetch(`${BACKEND_URL}/api/upload/dive-center-image`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}` },
                            body: formData
                          });
                          
                          const data = await response.json();
                          setCenterForm({...centerForm, image: data.image_url});
                        } catch (error) {
                          console.error("Error uploading image:", error);
                        }
                      }}
                    />
                    {centerForm.image && (
                      <div className="mt-2">
                        <img src={`${BACKEND_URL}${centerForm.image}`} alt="Preview" className="h-20 w-auto rounded" />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input value={centerForm.website} onChange={(e) => setCenterForm({...centerForm, website: e.target.value})} placeholder="https://..." />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={centerForm.email} onChange={(e) => setCenterForm({...centerForm, email: e.target.value})} type="email" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={centerForm.phone} onChange={(e) => setCenterForm({...centerForm, phone: e.target.value})} />
                  </div>
                  <div>
                    <Label>Amenities (comma-separated)</Label>
                    <Input value={centerForm.amenities} onChange={(e) => setCenterForm({...centerForm, amenities: e.target.value})} placeholder="PADI courses, Equipment rental, etc." />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label>Dive Center *</Label>
                    <select 
                      className="w-full border rounded-md p-2" 
                      value={siteForm.dive_center_id}
                      onChange={(e) => handleCenterSelection(e.target.value)}
                    >
                      <option value="">Select a dive center</option>
                      {diveCenters.map(center => (
                        <option key={center.id} value={center.id}>{center.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">💡 Map will auto-zoom to selected dive center</p>
                  </div>
                  <div>
                    <Label>Name *</Label>
                    <Input value={siteForm.name} onChange={(e) => setSiteForm({...siteForm, name: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Latitude</Label>
                      <Input 
                        type="number" 
                        step="0.000001"
                        value={newCoordinates?.lat || ''} 
                        onChange={(e) => {
                          const newLat = parseFloat(e.target.value);
                          if (!isNaN(newLat)) {
                            setNewCoordinates({
                              lat: newLat,
                              lng: newCoordinates?.lng || 0
                            });
                          }
                        }}
                        placeholder="e.g., 8.5234"
                      />
                    </div>
                    <div>
                      <Label>Longitude</Label>
                      <Input 
                        type="number" 
                        step="0.000001"
                        value={newCoordinates?.lng || ''} 
                        onChange={(e) => {
                          const newLng = parseFloat(e.target.value);
                          if (!isNaN(newLng)) {
                            setNewCoordinates({
                              lat: newCoordinates?.lat || 0,
                              lng: newLng
                            });
                          }
                        }}
                        placeholder="e.g., 123.4567"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Rating (0-5)</Label>
                    <Input value={siteForm.rating} onChange={(e) => setSiteForm({...siteForm, rating: e.target.value})} type="number" step="0.1" min="0" max="5" />
                  </div>
                  <div>
                    <Label>Main Image</Label>
                    <input 
                      type="file" 
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const formData = new FormData();
                          formData.append('image', file);
                          try {
                            const response = await fetch(`${BACKEND_URL}/api/upload/dive-site-image`, {
                              method: 'POST',
                              body: formData
                            });
                            const data = await response.json();
                            setSiteForm({...siteForm, image: data.image_url});
                          } catch (error) {
                            console.error("Error uploading image:", error);
                          }
                        }
                      }}
                      className="w-full border rounded-md p-2"
                    />
                    {siteForm.image && (
                      <div className="mt-2">
                        <img 
                          src={siteForm.image.startsWith('http') ? siteForm.image : `${BACKEND_URL}${siteForm.image}`} 
                          alt="Preview" 
                          className="h-32 w-auto rounded border" 
                        />
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => setSiteForm({...siteForm, image: ''})}
                          className="mt-2"
                        >
                          Remove Image
                        </Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <MultiImageUpload
                      images={siteForm.images}
                      onImagesChange={(urls) => setSiteForm({...siteForm, images: urls})}
                      uploadEndpoint="/api/upload/dive-site-image"
                      maxImages={5}
                      label="Additional Images (Gallery)"
                    />
                  </div>
                  <div>
                    <Label>Depth</Label>
                    <Input value={siteForm.depth} onChange={(e) => setSiteForm({...siteForm, depth: e.target.value})} placeholder="e.g., 5-40m" />
                  </div>
                  <div>
                    <Label>Difficulty</Label>
                    <Input value={siteForm.difficulty} onChange={(e) => setSiteForm({...siteForm, difficulty: e.target.value})} placeholder="e.g., All levels, Advanced" />
                  </div>
                  <div>
                    <Label>Marine Life (comma-separated)</Label>
                    <Input value={siteForm.marine_life} onChange={(e) => setSiteForm({...siteForm, marine_life: e.target.value})} placeholder="Sea turtles, Barracudas, etc." />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={siteForm.description} onChange={(e) => setSiteForm({...siteForm, description: e.target.value})} rows={3} />
                  </div>
                </>
              )}

              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={mode === 'dive-centers' ? handleSaveDiveCenter : handleSaveDiveSite}
                  className="flex-1"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button variant="outline" onClick={handleCancelAdd} className="flex-1">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminMapEditor;
